import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Table,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";

// Rule: allowed MIME types for import files
const ALLOWED_MIME_TYPES = [
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "text/csv", // .csv
  "application/csv",
];
// Rule: allowed file extensions
const ALLOWED_EXTENSIONS = [".xls", ".xlsx", ".csv"];
// Rule: max file size 2MB
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

export default function ImportEmployees() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // ---------- File validation handler ----------
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(null);
    setFileError("");
    setImportResult(null);

    if (!file) return;

    // Rule: check file extension (case-insensitive)
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setFileError("Only .xls, .xlsx, and .csv files are allowed.");
      e.target.value = "";
      return;
    }

    // Rule: check MIME type
    if (file.type && !ALLOWED_MIME_TYPES.includes(file.type)) {
      setFileError("Invalid file type. Please upload an Excel or CSV file.");
      e.target.value = "";
      return;
    }

    // Rule: enforce 2MB size limit
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError("File size must not exceed 2MB.");
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  // ---------- Download sample file ----------
  const downloadSample = () => {
    const headers = [
      "First Name",
      "Last Name",
      "Staff Id",
      "Username",
      "Email",
      "Password",
      "Joining Date",
      "Gender",
      "Date of Birth",
      "Contact Number",
      "Address",
      "City",
      "Zip",
      "Country",
      "Attendance Type",
      "Company Name",
      "Department Name",
      "Designation Name",
      "Shift Name",
      "Role Name",
    ];
    const sampleRow = [
      "John",
      "Doe",
      "EMP001",
      "johndoe",
      "john@example.com",
      "Password123",
      "2024-01-15",
      "Male",
      "1990-05-20",
      "1234567890",
      "123 Main St",
      "New York",
      "10001",
      "USA",
      "general",
      "My Company",
      "IT Department",
      "Software Engineer",
      "Morning Shift",
      "Employee",
    ];
    const csvContent = [headers.join(","), sampleRow.join(",")].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "employee_import_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ---------- Upload file ----------
  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await api.post("/api/employees/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(response.data.message);
        // Handle new all-or-nothing response format
        setImportResult({
          success: response.data.imported || 0,
          failed: response.data.failed || 0,
          totalRows: response.data.totalRows || 0,
          errors: response.data.errors || [],
          employees: response.data.employees || [],
          allOrNothing: response.data.allOrNothing,
        });
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById("formFile");
        if (fileInput) fileInput.value = "";
      } else {
        toast.error(response.data.message || "Import failed");
      }
    } catch (error) {
      console.error("Import error:", error);
      const message =
        error.response?.data?.message || error.message || "Import failed";
      toast.error(message);
      // Handle all-or-nothing error response with detailed errors
      if (error.response?.data) {
        setImportResult({
          success: 0,
          failed:
            error.response.data.failed || error.response.data.totalRows || 0,
          totalRows: error.response.data.totalRows || 0,
          errors: error.response.data.errors || [],
          allOrNothing: true,
          aborted: true,
        });
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Breadcrumb
        routeSegments={[
          { name: "Employees", path: "/employees" },
          { name: "Import Employees" },
        ]}
      />

      <Card
        body
        style={{
          borderRadius: "10px",
          padding: "20px",
          marginTop: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <h5 style={{ marginBottom: "15px" }}>Import EXCEL/CSV file only</h5>

        {/* Download Sample Button */}
        <Row className="mb-3">
          <Col md="auto">
            <Button
              onClick={downloadSample}
              style={{
                backgroundColor: "#6f42c1",
                borderColor: "#6f42c1",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "5px",
                padding: "8px 16px",
              }}
            >
              <i className="i-Download"></i>
              Download Sample File
            </Button>
          </Col>
        </Row>

        {/* Instructions */}
        <p
          style={{ fontSize: "14px", color: "#959292ff", marginBottom: "10px" }}
        >
          The first line in downloaded sample file should remain as it is.
          Please do not change the order of columns in file.
        </p>
        <p
          style={{ fontSize: "14px", color: "#959292ff", marginBottom: "10px" }}
        >
          The correct column order is (First Name, Last Name, Staff Id,
          Username, Email, Password, Joining Date, Gender, Date of Birth,
          Contact Number, Address, City, Zip, County, Attendance Type, Company
          Name, Department Name, Designation Name, Shift Name, Role Name).
        </p>
        <ul
          style={{
            fontSize: "14px",
            color: "#0c0c0cff",
            paddingLeft: "20px",
            marginBottom: "20px",
          }}
        >
          <li>Date format should be (According to general settings)</li>
          <li>
            Company, Department, Designation, Shift and Role name must be
            matched with your existing data.
          </li>
          <li>Gender must be Male / Female / Other.</li>
          <li>Attendance Type must be general / ip_based.</li>
          <li>
            You must follow the file, otherwise you will get an error while
            importing the file.
          </li>
        </ul>

        {/* File Upload */}
        <Form.Group controlId="formFile" className="mb-3">
          <Form.Label>
            Upload File <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="file"
            // Rule: browser-level hint — only show Excel/CSV in picker
            accept=".xls,.xlsx,.csv"
            onChange={handleFileChange}
            isInvalid={!!fileError}
            style={{
              borderRadius: "5px",
              padding: "6px 12px",
            }}
          />
          {/* Inline error message when file is invalid */}
          <Form.Control.Feedback type="invalid">
            {fileError}
          </Form.Control.Feedback>
          <small style={{ fontSize: "12px", color: "#777" }}>
            Please select excel/csv file (allowed file size 2MB)
          </small>
        </Form.Group>

        {/* Save Button — disabled until a valid file is selected */}
        <Row>
          <Col md="auto">
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              style={{
                backgroundColor: "#6f42c1",
                borderColor: "#6f42c1",
                color: "#fff",
                borderRadius: "5px",
                padding: "8px 16px",
                opacity: !selectedFile || uploading ? 0.65 : 1,
              }}
            >
              {uploading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginRight: "8px" }}
                  />
                  Uploading...
                </>
              ) : (
                <>
                  <i className="i-Data-Save" style={{ marginRight: "8px" }}></i>
                  Save
                </>
              )}
            </Button>
          </Col>
        </Row>

        {/* Import Results - All or Nothing */}
        {importResult && (
          <div style={{ marginTop: "24px" }}>
            {/* Summary Alert */}
            {importResult.aborted ? (
              <Alert variant="danger">
                <strong>❌ Import ABORTED</strong> - No data was imported
                <div style={{ marginTop: "8px", fontSize: "14px" }}>
                  {importResult.failed} of {importResult.totalRows} row(s)
                  failed validation.
                  <br />
                  <strong>Fix all errors and try again.</strong>
                </div>
              </Alert>
            ) : importResult.failed > 0 ? (
              <Alert variant="warning">
                <strong>⚠️ Import FAILED</strong> - All changes rolled back
                <div style={{ marginTop: "8px", fontSize: "14px" }}>
                  Database error occurred. No employees were imported.
                </div>
              </Alert>
            ) : (
              <Alert variant="success">
                <strong>✅ Import SUCCESSFUL</strong>
                <div style={{ marginTop: "8px", fontSize: "14px" }}>
                  {importResult.success} employee(s) imported successfully
                  {importResult.totalRows > 0 &&
                    ` (${importResult.totalRows} total rows)`}
                </div>
              </Alert>
            )}

            {/* Detailed Errors Table */}
            {importResult.errors && importResult.errors.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <h6 style={{ color: "#c62828", marginBottom: "12px" }}>
                  Validation Errors (Fix these and re-import):
                </h6>
                <div
                  style={{
                    maxHeight: "400px",
                    overflow: "auto",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                >
                  <Table
                    bordered
                    size="sm"
                    responsive
                    style={{ marginBottom: 0 }}
                  >
                    <thead
                      style={{
                        backgroundColor: "#ffebee",
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      <tr>
                        <th style={{ width: "80px" }}>Row</th>
                        <th>Error Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.errors.map((err, idx) => (
                        <tr key={idx}>
                          <td
                            style={{ fontWeight: "bold", textAlign: "center" }}
                          >
                            {err.row}
                          </td>
                          <td style={{ color: "#c62828" }}>{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {importResult.errors.length > 10 && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "8px",
                    }}
                  >
                    Showing all {importResult.errors.length} errors
                  </p>
                )}
              </div>
            )}

            {/* Successfully Imported Employees */}
            {importResult.employees && importResult.employees.length > 0 && (
              <div style={{ marginTop: "24px" }}>
                <h6 style={{ color: "#2e7d32", marginBottom: "12px" }}>
                  ✅ Successfully Imported Employees:
                </h6>
                <div
                  style={{
                    maxHeight: "300px",
                    overflow: "auto",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                >
                  <Table
                    bordered
                    size="sm"
                    responsive
                    style={{ marginBottom: 0 }}
                  >
                    <thead
                      style={{
                        backgroundColor: "#e8f5e9",
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      <tr>
                        <th>Row</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Staff ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.employees.map((emp, idx) => (
                        <tr key={idx}>
                          <td>{emp.row}</td>
                          <td>{emp.name}</td>
                          <td>{emp.email}</td>
                          <td>{emp.staffId || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
